import { useMemo, useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { errorMessage } from '../services/api'
import { fetchLeaguePlayers } from '../services/league'
import {
  applyPlayerMerge,
  fetchMergeCandidates,
  fetchMergeHistory,
  previewPlayerMerge,
  type ConferenceResolution,
  type MergeAuditEntry,
  type MergeCandidate,
  type MergePreview,
  type MergeSide,
  type ReconciliationPlayer,
} from '../services/playerMerge'
import {
  Card,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  SortableHeaderRow,
  dateSortValue,
  formatDate,
  useTableSort,
  type SortableColumn,
} from './ui'
import type { Conference } from '../types'

/**
 * Admin tooling for folding duplicate competitor identities together.
 *
 * @remarks
 * A merge deletes a row and repoints every reference to it, and there is no
 * undo. The whole panel is therefore built around one rule: nothing is written
 * until the owner has seen a preview of exactly what will change. Suggested
 * pairs are ranked by name similarity but never applied on that basis — a
 * suggestion only opens the preview.
 */

/** A score this high means the two spellings are near-identical. */
const STRONG_MATCH = 0.9

function seasonList(seasons: number[]): string {
  return seasons.length ? seasons.join(', ') : '—'
}

/** Identity badge showing what the site renders plus what is underneath it. */
function IdentitySummary({ side, role }: { side: MergeSide; role: 'source' | 'target' }) {
  return (
    <div className="stack-sm">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <PlayerCell name={side.displayName} discordId={side.discordId} avatar={side.discordAvatar} />
        <span className={role === 'source' ? 'badge badge--loss' : 'badge badge--win'}>
          {role === 'source' ? 'Deleted' : 'Kept'}
        </span>
      </div>

      <div className="muted" style={{ fontSize: 13, lineHeight: 1.7 }}>
        <div>
          {side.linked ? (
            <>
              Discord <strong>{side.discordUsername ?? side.discordDisplayName}</strong>
            </>
          ) : (
            <span className="badge">No Discord account</span>
          )}
        </div>
        {side.leagueDisplayName !== null && (
          <div>
            League name <strong>{side.leagueDisplayName}</strong>, Discord name{' '}
            <strong>{side.discordDisplayName}</strong>
          </div>
        )}
        <div>
          {side.games} game{side.games === 1 ? '' : 's'}, {side.wins} won
        </div>
        <div>Played in seasons {seasonList(side.gameSeasons)}</div>
        <div>Rostered in seasons {seasonList(side.rosterSeasons)}</div>
        {side.titleSeasons.length > 0 && (
          <div>Champion of seasons {seasonList(side.titleSeasons)}</div>
        )}
        {side.aliases.length > 0 && <div>Import aliases: {side.aliases.join(', ')}</div>}
      </div>
    </div>
  )
}

/**
 * The confirm step: every consequence of one merge, and the decisions it needs.
 *
 * @param onDone - Called after a successful merge so the parent can reload.
 *
 * @remarks
 * Conference disagreements are rendered as a required choice rather than a
 * warning. The server refuses the merge until each one is answered, so leaving
 * them as advisory text would only produce a confusing rejection later.
 */
function MergePreviewCard({
  preview,
  onCancel,
  onDone,
}: {
  preview: MergePreview
  onCancel: () => void
  onDone: (result: MergeAuditEntry) => void
}) {
  const [resolutions, setResolutions] = useState<Record<number, Conference>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const unresolved = preview.conferenceConflicts.filter(
    entry => resolutions[entry.seasonNumber] === undefined
  )
  const ready = preview.canMerge && unresolved.length === 0

  async function confirm(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const chosen: ConferenceResolution[] = preview.conferenceConflicts.map(entry => ({
        seasonNumber: entry.seasonNumber,
        conference: resolutions[entry.seasonNumber],
      }))
      onDone(await applyPlayerMerge(preview.source.id, preview.target.id, chosen))
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Confirm this merge"
      subtitle="Nothing has been written yet. This cannot be undone once you confirm."
      actions={
        <div className="row">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger btn--sm"
            disabled={!ready || busy}
            title={
              preview.canMerge
                ? unresolved.length
                  ? 'Choose a conference for every disputed season first'
                  : undefined
                : 'This merge is refused — see the reason above'
            }
            onClick={() => void confirm()}
          >
            {busy ? 'Merging…' : `Merge ${preview.source.displayName} into ${preview.target.displayName}`}
          </button>
        </div>
      }
    >
      <div className="stack">
        {preview.blockers.map(blocker => (
          <Notice key={blocker.code + blocker.message} tone="error">
            {blocker.message}
          </Notice>
        ))}
        {error && <Notice tone="error">{error}</Notice>}

        <div className="grid-2">
          <IdentitySummary side={preview.source} role="source" />
          <IdentitySummary side={preview.target} role="target" />
        </div>

        <div className="grid-3">
          <div>
            <div className="stat__label">Games moving</div>
            <div className="stat__value">{preview.gameParticipantsMoving}</div>
          </div>
          <div>
            <div className="stat__label">Wins moving</div>
            <div className="stat__value">{preview.winnerRowsMoving}</div>
          </div>
          <div>
            <div className="stat__label">Titles moving</div>
            <div className="stat__value">
              {preview.championRowsMoving + preview.runnerUpRowsMoving}
            </div>
          </div>
        </div>

        {preview.sharedGames.length > 0 && (
          <div>
            <h3 style={{ marginBottom: 8 }}>Games both players appear in</h3>
            <ul className="skip-list">
              {preview.sharedGames.slice(0, 10).map(game => (
                <li key={game.gameId}>
                  Game #{game.gameId}, season {game.seasonNumber}, {game.homeScore}–
                  {game.awayScore} ({game.status})
                </li>
              ))}
            </ul>
          </div>
        )}

        {preview.seasonPlan.length > 0 && (
          <div>
            <h3 style={{ marginBottom: 8 }}>Conference memberships</h3>
            {/*
              Not sortable. This is the confirm step of an irreversible write,
              a handful of rows long, and some of those rows carry the
              conflict toggles the merge is blocked on. Moving them while the
              owner is answering is the wrong kind of surprise.
            */}
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="num">Season</th>
                    <th>{preview.source.displayName}</th>
                    <th>{preview.target.displayName}</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.seasonPlan.map(entry => (
                    <tr key={entry.seasonNumber}>
                      <td className="num">{entry.seasonNumber}</td>
                      <td>
                        <span
                          className={
                            entry.sourceConference === 'AFC' ? 'badge badge--afc' : 'badge badge--nfc'
                          }
                        >
                          {entry.sourceConference}
                        </span>
                      </td>
                      <td>
                        {entry.targetConference === null ? (
                          <span className="muted">not rostered</span>
                        ) : (
                          <span
                            className={
                              entry.targetConference === 'AFC'
                                ? 'badge badge--afc'
                                : 'badge badge--nfc'
                            }
                          >
                            {entry.targetConference}
                          </span>
                        )}
                      </td>
                      <td>
                        {entry.action === 'move' && (
                          <span className="muted">membership moves across</span>
                        )}
                        {entry.action === 'keep-target' && (
                          <span className="muted">
                            already agrees — the duplicate row is dropped
                          </span>
                        )}
                        {entry.action === 'conflict' && (
                          <div className="row">
                            <span className="badge badge--loss">Conflict</span>
                            <div className="segmented">
                              {(['AFC', 'NFC'] as const).map(option => (
                                <button
                                  key={option}
                                  type="button"
                                  className={option === 'AFC' ? 'is-afc' : 'is-nfc'}
                                  aria-pressed={resolutions[entry.seasonNumber] === option}
                                  onClick={() =>
                                    setResolutions(previous => ({
                                      ...previous,
                                      [entry.seasonNumber]: option,
                                    }))
                                  }
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.conferenceConflicts.length > 0 && (
              <p className="skip-list-hint">
                These two identities are rostered in different conferences for the same season, so
                one of them is wrong. Pick the conference the merged player should keep. Games
                already played keep the conference they were recorded under.
              </p>
            )}
          </div>
        )}

        {(preview.aliasesToRecord.length > 0 || preview.aliasesMoving.length > 0) && (
          <Notice>
            {preview.aliasesToRecord.length > 0 && (
              <>
                A future import that says{' '}
                {preview.aliasesToRecord.map(alias => (
                  <code key={alias}>{alias}</code>
                ))}{' '}
                will resolve to {preview.target.displayName} instead of creating a duplicate.{' '}
              </>
            )}
            {preview.aliasesMoving.length > 0 && (
              <>
                {preview.aliasesMoving.length} existing alias
                {preview.aliasesMoving.length === 1 ? '' : 'es'} follow across:{' '}
                {preview.aliasesMoving.join(', ')}.
              </>
            )}
          </Notice>
        )}
      </div>
    </Card>
  )
}

/** One proposed pair, with the evidence needed to accept or dismiss it. */
function CandidateRow({
  source,
  candidate,
  onReview,
}: {
  source: ReconciliationPlayer
  candidate: MergeCandidate
  onReview: () => void
}) {
  return (
    <tr>
      <td>
        <PlayerCell name={source.displayName} discordId={null} avatar={null} />
        <div className="muted" style={{ fontSize: 12 }}>
          {source.games} games · seasons {seasonList(source.gameSeasons)}
        </div>
      </td>
      <td>
        <PlayerCell
          name={candidate.displayName}
          discordId={candidate.discordId}
          avatar={candidate.discordAvatar}
        />
        <div className="muted" style={{ fontSize: 12 }}>
          {candidate.linked ? (
            <>Discord {candidate.discordDisplayName}</>
          ) : (
            'no Discord account'
          )}{' '}
          · {candidate.games} games · seasons {seasonList(candidate.gameSeasons)}
        </div>
      </td>
      <td>
        <span
          className={
            candidate.similarity.score >= STRONG_MATCH ? 'badge badge--win' : 'badge badge--pending'
          }
          title={candidate.similarity.reason}
        >
          {candidate.similarity.score.toFixed(2)}
        </span>
        <div className="muted" style={{ fontSize: 12 }}>
          {candidate.similarity.reason}
        </div>
      </td>
      <td className="num">
        {candidate.blockedBySharedGame ? (
          <span className="badge badge--loss" title="They played each other, so they are two people">
            Played each other
          </span>
        ) : (
          <button type="button" className="btn btn--sm" onClick={onReview}>
            Review
          </button>
        )}
      </td>
    </tr>
  )
}

/** One suggestion, flattened out of its source grouping so the table can sort. */
interface CandidatePair {
  source: ReconciliationPlayer
  candidate: MergeCandidate
}

const CANDIDATE_COLUMNS: Array<SortableColumn<CandidatePair>> = [
  { key: 'source', label: 'Duplicate to remove', sort: pair => pair.source.displayName },
  { key: 'target', label: 'Merge into', sort: pair => pair.candidate.displayName },
  {
    key: 'match',
    label: 'Match',
    sortLabel: 'match score',
    sort: pair => pair.candidate.similarity.score,
  },
  { key: 'actions' },
]

function CandidateTable({
  pairs,
  onReview,
}: {
  pairs: CandidatePair[]
  onReview: (pair: CandidatePair) => void
}) {
  const sort = useTableSort(pairs, CANDIDATE_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={CANDIDATE_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(pair => (
            <CandidateRow
              key={`${pair.source.id}-${pair.candidate.id}`}
              source={pair.source}
              candidate={pair.candidate}
              onReview={() => onReview(pair)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const MERGE_HISTORY_COLUMNS: Array<SortableColumn<MergeAuditEntry>> = [
  { key: 'when', label: 'When', sort: entry => dateSortValue(entry.createdAt) },
  { key: 'removed', label: 'Removed', sort: entry => entry.sourceDisplayName },
  { key: 'kept', label: 'Kept', sort: entry => entry.targetDisplayName },
  {
    key: 'games',
    label: 'Games',
    className: 'num',
    sortLabel: 'games moved',
    sort: entry => entry.gameParticipantsMoved,
  },
  {
    key: 'seasons',
    label: 'Seasons',
    className: 'num',
    sortLabel: 'memberships touched',
    sort: entry => entry.membershipsMoved + entry.membershipsDropped,
  },
  {
    key: 'aliases',
    label: 'Aliases recorded',
    sort: entry => entry.aliasesRecorded.join(', '),
  },
  { key: 'by', label: 'By', sortLabel: 'admin', sort: entry => entry.performedBy },
]

function MergeHistoryTable({ entries }: { entries: MergeAuditEntry[] }) {
  const sort = useTableSort(entries, MERGE_HISTORY_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={MERGE_HISTORY_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(entry => (
            <tr key={entry.id}>
              <td className="muted">{formatDate(entry.createdAt)}</td>
              <td>
                {entry.sourceDisplayName}{' '}
                <span className="muted">#{entry.sourceLeaguePlayerId}</span>
              </td>
              <td>
                {entry.targetDisplayName}{' '}
                <span className="muted">#{entry.targetLeaguePlayerId ?? '—'}</span>
              </td>
              <td className="num">{entry.gameParticipantsMoved}</td>
              <td className="num">{entry.membershipsMoved + entry.membershipsDropped}</td>
              <td className="muted">
                {entry.aliasesRecorded.length ? entry.aliasesRecorded.join(', ') : '—'}
              </td>
              <td className="muted">{entry.performedBy ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function PlayerMergePanel() {
  const candidates = useAsync(fetchMergeCandidates, [])
  const players = useAsync(fetchLeaguePlayers, [])
  const history = useAsync(fetchMergeHistory, [])

  const [preview, setPreview] = useState<MergePreview | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<MergeAuditEntry | null>(null)
  const [manualSource, setManualSource] = useState('')
  const [manualTarget, setManualTarget] = useState('')

  const suggested = useMemo(
    () =>
      (candidates.data ?? []).flatMap(group =>
        group.candidates.map(candidate => ({ source: group.source, candidate }))
      ),
    [candidates.data]
  )
  const noCandidate = useMemo(
    () => (candidates.data ?? []).filter(group => group.candidates.length === 0),
    [candidates.data]
  )

  const roster = players.data ?? []
  const unlinked = roster.filter(player => player.userId === null)

  async function openPreview(sourceId: number, targetId: number): Promise<void> {
    setLoadingPreview(true)
    setError(null)
    setResult(null)
    try {
      setPreview(await previewPlayerMerge(sourceId, targetId))
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setLoadingPreview(false)
    }
  }

  function finish(entry: MergeAuditEntry): void {
    setPreview(null)
    setResult(entry)
    candidates.reload()
    players.reload()
    history.reload()
  }

  return (
    <div className="stack">
      <Notice>
        A merge moves every game, membership, title, and alias from one identity to another and
        then deletes the first. It cannot be undone, so every merge is previewed first. Two
        Discord-linked accounts cannot be merged here: that would have to decide which login,
        session, and admin flag survives. Unlink one of them on the Users tab if they really are
        the same person.
      </Notice>

      {error && <Notice tone="error">{error}</Notice>}

      {result && (
        <Notice tone="success">
          Merged <strong>{result.sourceDisplayName}</strong> into{' '}
          <strong>{result.targetDisplayName}</strong>: {result.gameParticipantsMoved} game
          {result.gameParticipantsMoved === 1 ? '' : 's'}, {result.membershipsMoved} membership
          {result.membershipsMoved === 1 ? '' : 's'} moved, {result.membershipsDropped} duplicate
          membership{result.membershipsDropped === 1 ? '' : 's'} dropped,{' '}
          {result.winnerRowsMoved} win{result.winnerRowsMoved === 1 ? '' : 's'} reattributed
          {result.aliasesRecorded.length > 0 && (
            <> · future imports of {result.aliasesRecorded.join(', ')} now resolve automatically</>
          )}
          .
        </Notice>
      )}

      {loadingPreview && (
        <Card title="Building preview…">
          <Loading rows={3} />
        </Card>
      )}

      {preview && (
        <MergePreviewCard
          preview={preview}
          onCancel={() => setPreview(null)}
          onDone={finish}
        />
      )}

      <Card
        title="Likely duplicates"
        subtitle="Ranked by name similarity. A suggestion only opens the preview — nothing merges until you confirm."
        flush
      >
        {candidates.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : suggested.length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState title="No likely duplicates">
              Every unlinked identity has a name nothing else resembles. Run this again after the
              next historical import.
            </EmptyState>
          </div>
        ) : (
          <CandidateTable
            pairs={suggested}
            onReview={pair => void openPreview(pair.source.id, pair.candidate.id)}
          />
        )}
      </Card>

      <Card
        title="Merge two players by hand"
        subtitle="For pairs the name ranking cannot see, such as Disciple and honortogod"
      >
        <div className="row">
          <select
            className="select"
            style={{ minWidth: 220 }}
            value={manualSource}
            onChange={event => setManualSource(event.target.value)}
          >
            <option value="">Duplicate to remove…</option>
            {unlinked.map(player => (
              <option key={player.id} value={player.id}>
                {player.displayName}
              </option>
            ))}
          </select>
          <span className="muted">into</span>
          <select
            className="select"
            style={{ minWidth: 220 }}
            value={manualTarget}
            onChange={event => setManualTarget(event.target.value)}
          >
            <option value="">Player to keep…</option>
            {roster
              .filter(player => String(player.id) !== manualSource)
              .map(player => (
                <option key={player.id} value={player.id}>
                  {player.displayName}
                  {player.userId === null ? ' (no Discord)' : ''}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={!manualSource || !manualTarget}
            onClick={() => void openPreview(Number(manualSource), Number(manualTarget))}
          >
            Preview merge
          </button>
        </div>
        <p className="skip-list-hint">
          Only unlinked identities can be removed, so a Discord account can never be deleted by a
          merge.
        </p>
      </Card>

      {noCandidate.length > 0 && (
        <Card
          title="Unlinked players with no lookalike"
          subtitle="Most of these genuinely have no Discord account — leave them alone"
        >
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            {noCandidate.map(group => (
              <span key={group.source.id} className="badge" title={`${group.source.games} games`}>
                {group.source.displayName}
              </span>
            ))}
          </div>
        </Card>
      )}

      <Card
        title="Merge history"
        subtitle="A merge cannot be undone, so every one is recorded here"
        flush
      >
        {history.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={3} />
          </div>
        ) : (history.data ?? []).length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState title="No merges yet" />
          </div>
        ) : (
          <MergeHistoryTable entries={history.data ?? []} />
        )}
      </Card>
    </div>
  )
}
